import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';
import axios from 'axios';

interface SourceCount {
  source: string;
  count: number;
}

interface DailyView {
  day: string;
  count: number;
}

export interface RecentView {
  id: number;
  source: string;
  viewedAt: string;
  viewer: {
    companyName: string;
    role: string;
  };
}

interface ProfileViewsState {
  viewsThisWeek: number;
  totalViews: number;
  bySource: SourceCount[];
  dailyViews: DailyView[];
  recentViews: RecentView[];
  recentViewsTotal: number;
  loading: boolean;
  recentLoading: boolean;
  error: string | null;
}

const initialState: ProfileViewsState = {
  viewsThisWeek: 0,
  totalViews: 0,
  bySource: [],
  dailyViews: [],
  recentViews: [],
  recentViewsTotal: 0,
  loading: false,
  recentLoading: false,
  error: null,
};

export const fetchProfileViews = createAsyncThunk(
  'profileViews/fetchProfileViews',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/worker-profile/views');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile views');
      }
      return rejectWithValue('Failed to fetch profile views');
    }
  }
);

export const fetchRecentViews = createAsyncThunk(
  'profileViews/fetchRecentViews',
  async ({ limit = 5, offset = 0 }: { limit?: number; offset?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(`/worker-profile/views/recent?limit=${limit}&offset=${offset}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch recent views');
      }
      return rejectWithValue('Failed to fetch recent views');
    }
  }
);

export const recordProfileView = createAsyncThunk(
  'profileViews/recordProfileView',
  async ({ workerId, source }: { workerId: number; source: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/employer/workers/${workerId}/view`, { source });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to record view');
      }
      return rejectWithValue('Failed to record view');
    }
  }
);

const profileViewsSlice = createSlice({
  name: 'profileViews',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfileViews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfileViews.fulfilled, (state, action) => {
        state.loading = false;
        state.viewsThisWeek = action.payload?.viewsThisWeek ?? 0;
        state.totalViews = action.payload?.totalViews ?? 0;
        state.bySource = action.payload?.bySource ?? [];
        state.dailyViews = action.payload?.dailyViews ?? [];
      })
      .addCase(fetchProfileViews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchRecentViews.pending, (state) => {
        state.recentLoading = true;
      })
      .addCase(fetchRecentViews.fulfilled, (state, action) => {
        state.recentLoading = false;
        state.recentViews = action.payload?.views ?? [];
        state.recentViewsTotal = action.payload?.total ?? 0;
      })
      .addCase(fetchRecentViews.rejected, (state) => {
        state.recentLoading = false;
      });
  },
});

export default profileViewsSlice.reducer;
